import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { listMoments } from "@/data/lifestyle_moments";

/**
 * <ChooseYourVault/> — a 4-card persona picker under the headline
 * "Everyone's financial life looks a little different." Each card uses
 * a human still image (never a generated person) and links to the
 * matching demo/marketing route.
 */
export default function ChooseYourVault() {
    const items = listMoments("choose");
    if (!items.length) return null;
    return (
        <section className="cyv-section" data-testid="choose-your-vault" aria-label="Choose your VAULT">
            <style>{CYV_CSS}</style>
            <div className="cyv-container">
                <div className="cyv-eyebrow">
                    <Sparkles size={11} strokeWidth={2.2} /> CHOOSE YOUR VAULT
                </div>
                <h2 className="cyv-title">Everyone&rsquo;s financial life looks a little different.</h2>
                <p className="cyv-sub">Start with the story that sounds most like yours.</p>

                <div className="cyv-grid">
                    {items.map((m) => (
                        <Link
                            key={m.id}
                            to={m.cta?.to || "/"}
                            className="cyv-card"
                            data-testid={`choose-${m.id}`}
                            aria-label={`${m.eyebrow} — ${m.cta?.label}`}
                        >
                            <div className="cyv-media">
                                <img src={m.poster_desktop} alt={m.alt_description} className="cyv-poster" loading="lazy" />
                                <div className="cyv-scrim" aria-hidden="true" />
                                <div className="cyv-eyebrow cyv-eyebrow--overlay">{(m.eyebrow || "").toUpperCase()}</div>
                            </div>
                            <div className="cyv-body">
                                <div className="cyv-headline">{m.headline}</div>
                                {m.sub ? <div className="cyv-sub-copy">{m.sub}</div> : null}
                                <div className="cyv-cta">{m.cta?.label} <ArrowRight size={12} /></div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

const CYV_CSS = `
.cyv-section { padding: 80px 24px; background: #FBF7EE; color: #0F1E3A; }
.cyv-container { max-width: 1160px; margin: 0 auto; }
.cyv-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.cyv-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(28px, 4vw, 46px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; margin: 0 0 10px; max-width: 720px; }
.cyv-sub { font-size: 15px; color: #57534E; margin: 0 0 32px; max-width: 520px; }
.cyv-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
@media (max-width: 1000px) { .cyv-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px)  { .cyv-grid { grid-template-columns: 1fr; } }
.cyv-card { display: flex; flex-direction: column; border-radius: 18px; overflow: hidden; background: #fff; border: 1px solid rgba(15,30,58,0.10); text-decoration: none; color: inherit; transition: transform .24s cubic-bezier(0.16,1,0.3,1), box-shadow .24s cubic-bezier(0.16,1,0.3,1), border-color .24s cubic-bezier(0.16,1,0.3,1); }
.cyv-card:hover { transform: translateY(-4px); box-shadow: 0 22px 40px -20px rgba(15,30,58,0.28); border-color: rgba(198,168,107,0.4); }
.cyv-media { position: relative; aspect-ratio: 4 / 5; background: #0F1E3A; }
.cyv-poster { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.cyv-scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(15,30,58,0) 40%, rgba(15,30,58,0.75) 100%); }
.cyv-eyebrow--overlay { position: absolute; top: 14px; left: 14px; margin: 0; background: rgba(15,30,58,0.72); border: 1px solid rgba(230,213,168,0.4); border-radius: 999px; padding: 5px 10px; color: #E6D5A8; }
.cyv-body { padding: 20px 22px 22px; display: flex; flex-direction: column; }
.cyv-headline { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; line-height: 1.2; letter-spacing: -0.005em; margin: 0 0 10px; color: #0F1E3A; }
.cyv-sub-copy { font-size: 13.5px; line-height: 1.5; color: #57534E; margin: 0 0 16px; }
.cyv-cta { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: #0F1E3A; margin-top: auto; }
.cyv-card:hover .cyv-cta { color: #C6A86B; }
`;
